import { Modal } from '@everlywell/leaves';
import Cookies from 'js-cookie';
import React, { useEffect, useRef, useState } from 'react';
import { useTransition } from 'react-spring';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';

import Disclaimer from './ScreenerPages/Disclaimer';
import EligibleContent from './ScreenerPages/EligibleContent';
import ExposureQuestions from './ScreenerPages/ExposureQuestions';
import NotSickContent from './ScreenerPages/NotSickContent';
import SymptomQuestions from './ScreenerPages/SymptomQuestions';
import TooSickContent from './ScreenerPages/TooSickContent';
import * as S from './styles';

export type DisclaimerModalProps = {
  openHandler: (x: boolean) => void;
  open: boolean;
  productSku: string;
  selectedQuantity: number;
  handleAddToCart: (
    e: React.FormEvent<HTMLButtonElement>,
    callback?: () => void,
  ) => void;
};

export enum SymptomLevels {
  NoSymptoms = 'noSymptoms',
  MildSymptoms = 'mildSymptoms',
  SevereSymptoms = 'severeSymptoms',
}

export enum ExposureLevels {
  NoExposure = 'noExposure',
  PotentialExposure = 'potentialExposure',
  SuspectedExposure = 'suspectedExposure',
  KnownExposure = 'knownExposure',
}

export enum Steps {
  Disclaimer = 'disclaimer',
  Symptoms = 'symptoms',
  Exposure = 'exposure',
  TooSick = 'tooSick',
  NotSick = 'notSick',
  Eligible = 'eligible',
}

export const DisclaimerModal = ({
  openHandler,
  open,
  productSku,
  selectedQuantity = 1,
  handleAddToCart,
}: DisclaimerModalProps): JSX.Element | null => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState<string>(Steps.Disclaimer);
  const [symptomLevel, setSymptomLevel] = useState<string>('');
  const [exposureLevel, setExposureLevel] = useState<string>('');

  const trackButtonClick = (label: string) => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label,
        sku: productSku,
      },
    });
  };

  const trackPageView = (page: string) => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.VIEWED_PAGE,
      data: {
        page,
        sku: productSku,
      },
    });
  };

  const handleSymptomClick = (level: SymptomLevels) => {
    setSymptomLevel(level);
    switch (level) {
      case SymptomLevels.NoSymptoms:
        trackButtonClick(ANALYTICS.LABELS.NO_COVID_SYMPTOMS);
        setTimeout(() => {
          trackPageView(ANALYTICS.LABELS.INTAKE_EXPOSURE_COVID_SCREENER);
          setCurrentStep(Steps.Exposure);
        }, 500);
        return;
      case SymptomLevels.MildSymptoms:
        trackButtonClick(ANALYTICS.LABELS.MILD_COVID_SYMPTOMS);
        setTimeout(() => {
          trackPageView(ANALYTICS.LABELS.INTAKE_EXPOSURE_COVID_SCREENER);
          setCurrentStep(Steps.Exposure);
        }, 500);
        return;
      case SymptomLevels.SevereSymptoms:
        trackButtonClick(ANALYTICS.LABELS.SEVERE_COVID_SYMPTOMS);
        setTimeout(() => {
          trackPageView(ANALYTICS.LABELS.SEEK_MEDICAL_CARE_COVID_SCREENER);
          setCurrentStep(Steps.TooSick);
        }, 500);
        return;
      default:
        return;
    }
  };

  const handleExposureClick = (level: ExposureLevels) => {
    setExposureLevel(level);
    switch (level) {
      case ExposureLevels.NoExposure:
        trackButtonClick(ANALYTICS.LABELS.NO_COVID_EXPOSURE);
        if (symptomLevel === SymptomLevels.MildSymptoms) {
          setTimeout(() => {
            trackPageView(ANALYTICS.LABELS.INTAKE_COVID_CHECKOUT);
            setCurrentStep(Steps.Eligible);
          }, 500);
          return;
        }
        setTimeout(() => {
          trackPageView(ANALYTICS.LABELS.NOT_RECOMMENDED_COVID_SCREENER);
          setCurrentStep(Steps.NotSick);
        }, 500);
        return;
      case ExposureLevels.PotentialExposure:
        trackButtonClick(ANALYTICS.LABELS.POTENTIAL_COVID_EXPOSURE);
        setTimeout(() => {
          trackPageView(ANALYTICS.LABELS.INTAKE_COVID_CHECKOUT);
          setCurrentStep(Steps.Eligible);
        }, 500);
        return;
      case ExposureLevels.SuspectedExposure:
        trackButtonClick(ANALYTICS.LABELS.SUSPECTED_COVID_EXPOSURE);
        setTimeout(() => {
          trackPageView(ANALYTICS.LABELS.INTAKE_COVID_CHECKOUT);
          setCurrentStep(Steps.Eligible);
        }, 500);
        return;
      case ExposureLevels.KnownExposure:
        trackButtonClick(ANALYTICS.LABELS.KNOWN_COVID_EXPOSURE);
        setTimeout(() => {
          trackPageView(ANALYTICS.LABELS.INTAKE_COVID_CHECKOUT);
          setCurrentStep(Steps.Eligible);
        }, 500);
        return;
      default:
        return;
    }
  };

  const handleEligibleContentRender = () => {
    Cookies.set('covid-quiz', 'true');
  };

  // scroll to top of each "page" in the modal
  useEffect(() => {
    if (open && contentRef) {
      contentRef?.current?.scrollIntoView();
    }
  }, [open, contentRef, currentStep]);

  useEffect(() => {
    // TODO: hack to keep Header from covering Modal when opened from sticky Order button
    const header = document.querySelector('#everlywellHeader') as HTMLElement;
    if (open) {
      if (header) {
        header.style.zIndex = `1`;
      }
    } else {
      if (header) {
        header.style.zIndex = '2';
      }
      setCurrentStep(Steps.Disclaimer);
      setSymptomLevel('');
      setExposureLevel('');
    }
  }, [open]);

  const renderContent = (step: string) => {
    switch (step) {
      case Steps.Disclaimer:
        return (
          <Disclaimer
            handleAddToCart={handleAddToCart}
            openHandler={openHandler}
          />
        );
      case Steps.Symptoms:
        return (
          <SymptomQuestions
            symptomLevel={symptomLevel}
            handleSymptomClick={handleSymptomClick}
          />
        );
      case Steps.Exposure:
        return (
          <ExposureQuestions
            handleExposureClick={handleExposureClick}
            exposureLevel={exposureLevel}
          />
        );
      case Steps.TooSick:
        return <TooSickContent />;
      case Steps.NotSick:
        return <NotSickContent />;
      case Steps.Eligible:
        return <EligibleContent onRender={handleEligibleContentRender} />;
      default:
        return null;
    }
  };

  const contentTransition = useTransition(currentStep, {
    from: {
      opacity: -5,
    },
    enter: {
      position: 'static',
      opacity: 1,
    },
    leave: {
      position: 'absolute',
      opacity: 0,
      immediate: true,
    },
  });

  return open ? (
    <Modal openHandler={openHandler} open={open}>
      <div ref={contentRef}>
        <S.ModalInner>
          {contentTransition((values) => (
            <S.AnimatedContentWrapper style={values}>
              {renderContent(currentStep)}
            </S.AnimatedContentWrapper>
          ))}
        </S.ModalInner>
      </div>
    </Modal>
  ) : null;
};

export default DisclaimerModal;
